/**
 * Shared locale-switching helpers.
 *
 * The UI locale is a NEXT_LOCALE cookie read by `i18n/request.ts` on every
 * request — writing it once (from any switcher: the sidebar NavUser menu or
 * the onboarding top bar) carries the choice across the whole product, so
 * onboarding and post-onboarding stay in sync with no extra plumbing.
 *
 * Writing the cookie is not enough on its own. Every other open tab rendered
 * its own `useLocale()` value on the server and keeps that value until it
 * reloads, so it goes on sending the OLD locale with every chat request —
 * which is how a conversation switched to Chinese in one tab kept getting
 * English replies from another. `setLocaleCookie` therefore also tells the
 * other tabs to re-render.
 */
import type { Locale } from '@/i18n/config'

export const localeLabels: Record<Locale, string> = {
  en: 'English',
  zh: '中文',
  ar: 'العربية',
}

const LOCALE_BROADCAST_CHANNEL = 'nex-locale-change'
const LOCALE_STORAGE_KEY = 'nex-locale-change'

export function setLocaleCookie(locale: Locale) {
  document.cookie = `NEXT_LOCALE=${locale};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`
  broadcastLocaleChange(locale)
  window.location.reload()
}

function broadcastLocaleChange(locale: Locale) {
  try {
    const channel = new BroadcastChannel(LOCALE_BROADCAST_CHANNEL)
    channel.postMessage(locale)
    channel.close()
    return
  } catch {
    // BroadcastChannel missing or blocked — fall through to the storage event,
    // which reaches the other tabs of this origin just as well.
  }
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, `${locale}:${Date.now()}`)
  } catch {
    // Storage denied too (private mode, blocked site data). The other tabs
    // keep their stale locale until their next natural load; this tab still
    // reloads, so the switch the user just made is never silently lost.
  }
}

/**
 * Re-render this tab when another tab switches the locale. Returns an
 * unsubscribe function.
 *
 * The listener reloads rather than swapping messages in place because the
 * locale reaches the client through the server render (`i18n/request.ts` reads
 * the cookie per request); a reload is what makes the rendered UI and the
 * locale sent on subsequent requests change together instead of drifting apart.
 */
export function subscribeToLocaleChange(onChange: () => void): () => void {
  const cleanups: Array<() => void> = []

  try {
    const channel = new BroadcastChannel(LOCALE_BROADCAST_CHANNEL)
    channel.onmessage = () => onChange()
    cleanups.push(() => channel.close())
  } catch {
    // No BroadcastChannel here; the storage listener below still applies.
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === LOCALE_STORAGE_KEY) onChange()
  }
  window.addEventListener('storage', onStorage)
  cleanups.push(() => window.removeEventListener('storage', onStorage))

  return () => {
    for (const cleanup of cleanups) cleanup()
  }
}
